import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowPathIcon, ArrowRightIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import AuthShell from './AuthShell';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (formData.full_name.trim().length < 2) nextErrors.full_name = 'Enter your name';
    if (formData.username.trim().length < 3) nextErrors.username = 'Use at least 3 characters';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) nextErrors.email = 'Enter a valid email address';
    if (formData.password.length < 8) nextErrors.password = 'Use at least 8 characters';
    if (formData.password !== formData.confirmPassword) nextErrors.confirmPassword = 'Passwords do not match';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const { confirmPassword, ...userData } = formData;
    const result = await register({
      ...userData,
      username: userData.username.trim(),
      email: userData.email.trim(),
      full_name: userData.full_name.trim(),
    });

    if (result.success) {
      toast.success('Account created. Sign in to continue.');
      navigate('/login');
    } else {
      toast.error(result.error || 'Unable to create account');
    }

    setLoading(false);
  };

  const fieldClass = (name) => `field-input ${errors[name] ? 'border-[#c65f43] focus:border-[#c65f43] focus:ring-[#c65f43]/15' : ''}`;

  return (
    <AuthShell
      eyebrow="Create your workspace"
      title="Bring the situation together"
      description="Set up your account, create your first context, and connect the information you no longer want to reconstruct."
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="full_name" className="field-label">Full name</label>
            <input id="full_name" name="full_name" autoComplete="name" value={formData.full_name} onChange={handleChange} className={fieldClass('full_name')} placeholder="Your name" />
            {errors.full_name && <p className="mt-1 text-xs font-medium text-[#a54530]">{errors.full_name}</p>}
          </div>
          <div>
            <label htmlFor="username" className="field-label">Username</label>
            <input id="username" name="username" autoComplete="username" value={formData.username} onChange={handleChange} className={fieldClass('username')} placeholder="Choose a username" />
            {errors.username && <p className="mt-1 text-xs font-medium text-[#a54530]">{errors.username}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="field-label">Email address</label>
          <input id="email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} className={fieldClass('email')} placeholder="you@example.com" />
          {errors.email && <p className="mt-1 text-xs font-medium text-[#a54530]">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className="field-label">Password</label>
          <div className="relative">
            <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={formData.password} onChange={handleChange} className={`${fieldClass('password')} pr-12`} placeholder="At least 8 characters" />
            <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-md text-[#6d7871] hover:bg-[#edf1ed]" aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs font-medium text-[#a54530]">{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="field-label">Confirm password</label>
          <input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={formData.confirmPassword} onChange={handleChange} className={fieldClass('confirmPassword')} placeholder="Repeat your password" />
          {errors.confirmPassword && <p className="mt-1 text-xs font-medium text-[#a54530]">{errors.confirmPassword}</p>}
        </div>

        <button type="submit" disabled={loading} className="primary-button mt-2 w-full disabled:cursor-not-allowed disabled:opacity-60">
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Creating workspace
            </>
          ) : (
            <>
              Create account
              <ArrowRightIcon className="h-4 w-4" />
            </>
          )}
        </button>
      </form>

      <div className="mt-7 border-t border-[#dce3dd] pt-6 text-center text-sm text-[#65716a]">
        Already have an account?{' '}
        <Link to="/login" className="font-bold text-[#246b52] hover:text-[#18513d]">Sign in</Link>
      </div>
    </AuthShell>
  );
};

export default Register;
